import React, { useCallback, useMemo } from 'react'
import { compose } from 'redux'
import { Grid, Typography, withStyles } from '@material-ui/core'
import { ETAPE_TYPE } from '../../../../../../utils/constants'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import Title from '../../layout/Title'
import { BLOCK_ACTION_TYPE, SVB_CROPS_TABLE_NAME } from '../tool/suiviParBloc.constants'
import Button from '../../../../../../components/Button'
import ButtonWithMenu from '../../../../../../components/ButtonWithMenu'
import AddCommentIccon from '@material-ui/icons/AddComment'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import NoResultPage from '../../../../../../components/NoResultPage'
import SpringDataTableWithLoader from '../../../../../../components/layout/SpringDataTableWithLoader'
import { IN_PROGRESS_CROPS_URI } from '../service/suiviParBlocApi'
import { useTourPlaineContext } from '../../../TourPlaineContext'
import { toI18N } from '../../../../../../utils/utils'
import { Chat } from '@material-ui/icons'
import { injectReminderForm } from '../../../service/reminderFormInjector'
import { ACTIONS_KEY, TOUR_PLAINE_TABS, CROP_DATATABLE_KEYS, EMPTY_PLACEHOLDER, ETAPE_CROISSANCE } from '../../../tool/tourPlaine.constants'
import { injectActionsForm } from '../../../service/actionsFormInjector'
import { contentStyles } from '../tool/suiviParBloc.styles'
import { inProgressCropsToApp } from '../../../tool/tourPlaine.mapper'
import { formatDateToWeek } from '../../../tool/tourPlaine.utils'
import classnames from 'classnames'
import { colors } from '../../../../../../utils/constants'
import { connect } from 'react-redux'
import { dataTableActions } from 'isotope-client'

// Colonnes triables
const SORTABLE_KEYS = [CROP_DATATABLE_KEYS.BOARD, CROP_DATATABLE_KEYS.CROP]

/**
 * CropsContent for SuiviParBloc
 * @returns {JSX.Element}
 */
const CropsContent = ({ classes, block, openReminderForm, getActions, selectedRows, refreshTable, resetSelectedRows }) => {
	const { formatMessage } = useIntl()
	const { currentWeek, currentCampagneInProgress } = useTourPlaineContext()

	// Filtres envoyés en query params à l'endpoint paginé
	const filters = useMemo(() => ({ blocId: block.uniqueId, dateDebut: currentWeek }), [block.uniqueId, currentWeek])

	// Les actions du tour de plaine travaillent sur les cultures mappées
	const selectedCrops = useMemo(() => inProgressCropsToApp(selectedRows), [selectedRows])

	const refreshData = useCallback((shouldUnselect) => {
		if (shouldUnselect) {
			resetSelectedRows()
		}
		refreshTable()
	}, [resetSelectedRows, refreshTable])

	const headers = useMemo(() => [
		{
			key: CROP_DATATABLE_KEYS.BOARD,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.BOARD}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 80
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.BOARD),
			checked: true,
			render: row => (
				<Typography className={classes.dataTableBoldText}>
					{row.assolement?.planche?.nom}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.CROP,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.CROP}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.CROP),
			render: row => (
				<div className={classes.cell}>
					{row.assolement?.commentaires?.length ? <Chat style={{ color: colors.warning }} /> : null}
					<Typography className={classes.dataTableNormalText}>
						{row.assolement?.culture?.nom || EMPTY_PLACEHOLDER}
					</Typography>
				</div>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.STEP,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.STEP}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.STEP),
			render: row => {
				const stepType = Object.keys(ETAPE_TYPE).find(key => key === (row.etape || ETAPE_CROISSANCE))
				return (
					<Typography className={classnames(classes.dataTableNormalText, { [classes.italicText]: ETAPE_TYPE[stepType] === ETAPE_TYPE.CROISSANCE })}>
						{stepType && <FormattedMessage id={`enums.etape.${ETAPE_TYPE[stepType]}`} />}
					</Typography>
				)
			}
		},
		{
			key: CROP_DATATABLE_KEYS.START_HARVEST,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.START_HARVEST}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.START_HARVEST),
			render: row => (
				<Typography className={classes.dataTableNormalText}>
					{toI18N(formatDateToWeek(row.debutRecolte))}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.END_HARVEST,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.END_HARVEST}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.END_HARVEST),
			render: row => (
				<Typography className={classes.dataTableNormalText}>
					{toI18N(formatDateToWeek(row.finRecolte))}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.NEXT,
			name: <Typography className={classes.dataTableHeaderNext}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.NEXT}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.NEXT),
			render: row => (
				<Typography className={classes.dataTableBoardNext}>
					{row.assolementEnCoursOuSuivant?.culture?.nom || EMPTY_PLACEHOLDER}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.PLANNED_PREP_DATE,
			name: <Typography className={classes.dataTableHeaderNext}><FormattedMessage id={`tourPlaine.suiviParBloc.crops.table.headers.${CROP_DATATABLE_KEYS.PLANNED_PREP_DATE}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 140
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.PLANNED_PREP_DATE),
			render: row => (
				<Typography className={classes.dataTableBoardNext}>
					{toI18N(formatDateToWeek(row.debutPreparation))}
				</Typography>
			)
		}], [classes])

	const cropActions = useMemo(() => getActions([
				ACTIONS_KEY.DETAIL_CROP,
				ACTIONS_KEY.MOVE_TO_HARVEST_STEP,
				ACTIONS_KEY.MOVE_TO_IMPLANT_STEP,
				ACTIONS_KEY.ADD_PROD_TRACKING,
				ACTIONS_KEY.ADJUST_DURATION,
				ACTIONS_KEY.DELETE_CROP
			],
			selectedCrops,
			TOUR_PLAINE_TABS.SUIVI_BLOC,
			refreshData),
		[selectedCrops, getActions, refreshData])
	const currentOrNextCropActions = useMemo(() => getActions([
				ACTIONS_KEY.DETAIL_CROP_CURRENT_OR_NEXT,
				ACTIONS_KEY.MOVE_BOARD_STEP,
				ACTIONS_KEY.DELETE_CROP_CURRENT_OR_NEXT
			],
			selectedCrops,
			TOUR_PLAINE_TABS.SUIVI_BLOC,
			refreshData),
		[selectedCrops, getActions, refreshData])

	return (
		<div className={classes.root}>
			<Grid item container className={classes.header}>
				<Title actionBlock={BLOCK_ACTION_TYPE.CROPS} />
				<Grid item container direction="row" justify="flex-start" alignItems="flex-end">
					<Grid item>
						<Typography className={classes.descriptionText}>
							<FormattedMessage id="tourPlaine.suiviParBloc.crops.description" />
						</Typography>
						<ul className={classes.ulContainer}>
							<li>
								<Typography className={classes.descriptionText}>
									<FormattedMessage id="tourPlaine.suiviParBloc.crops.description1" />
								</Typography>
							</li>
							<li>
								<Typography className={classes.descriptionText}>
									<FormattedMessage id="tourPlaine.suiviParBloc.crops.description2" />
								</Typography>
							</li>
						</ul>
					</Grid>
					{currentCampagneInProgress && <Grid item container xs direction="row" justify="flex-end" alignItems="flex-end" className={classes.actionContainer}>
						<Button
							type="secondary"
							startIcon={<AddCommentIccon />}
							onClick={() => openReminderForm(`${formatMessage({ id: 'tourPlaine.tabBar.suiviBlock' })} / ${formatMessage({ id: 'tourPlaine.suiviParBloc.actionTab.crops' })}`)}
						>
							<FormattedMessage id="tourPlaine.actions.addReminder" />
						</Button>
						<ButtonWithMenu
							type="primary"
							startIcon={<MoreHorizIcon />}
							items={[
								{
									label: <FormattedMessage id="tourPlaine.actions.cultureCrop.suiviParBloc" />,
									isTitle: true
								},
								...cropActions,
								{
									isDivider: true
								},
								{
									label: <FormattedMessage id="tourPlaine.actions.nextCrop.suiviParBloc" />,
									isTitle: true
								},
								...currentOrNextCropActions
							]}
							disabled={!selectedRows.length}
						>
							<FormattedMessage id="tourPlaine.actions.others" />
						</ButtonWithMenu>
					</Grid>}
				</Grid>
			</Grid>
			<Grid item container className={classes.dataTableContainer}>
				<SpringDataTableWithLoader
					nom={SVB_CROPS_TABLE_NAME}
					apiUrl={IN_PROGRESS_CROPS_URI}
					filters={filters}
					pageSizes={[25, 50, 100]}
					defaultPageSize={25}
					tableProps={{
						stickyHeader: true,
						className: classes.dataTable
					}}
					noResultFragment={<NoResultPage emptyStateMessageId="tourPlaine.suiviParBloc.crops.table.empty" />}
					selectable={currentCampagneInProgress}
					displaySelectAll={currentCampagneInProgress}
					headers={headers}
					loaderStyle={{ width: '100%' }}
					messageStyle={{ verticalAlign: 'top', paddingTop: 72 }}
					hideContentOnLoad={false}
					getRowKey={row => row.assolement.id}
				/>
			</Grid>
		</div>
	)
}

CropsContent.propTypes = {
	classes: PropTypes.object,
	block: PropTypes.object,
	openReminderForm: PropTypes.func,
	getActions: PropTypes.func,
	selectedRows: PropTypes.array,
	refreshTable: PropTypes.func,
	resetSelectedRows: PropTypes.func
}

const mapStateToProps = (state) => ({
	selectedRows: (state.dataTable[SVB_CROPS_TABLE_NAME] || {}).selectedRows || []
})

const tableActions = {
	refreshTable: () => dataTableActions.refresh(SVB_CROPS_TABLE_NAME),
	resetSelectedRows: () => dataTableActions.selectRows(SVB_CROPS_TABLE_NAME, [])
}

export default compose(
	injectReminderForm,
	injectActionsForm,
	connect(mapStateToProps, tableActions),
	withStyles(contentStyles)
)(CropsContent)
